const getBillingMonths = (metadata, plan) => {
  if (metadata.numberOfMonth) {
    return parseInt(String(metadata.numberOfMonth), 10) || 1;
  }
  const recurring = plan.recurring || {};
  const interval = recurring.interval || plan.interval;
  const intervalCount = recurring.interval_count || plan.interval_count || 1;

  if (metadata.type === '4') return 1;
  if (metadata.type === '1') return 3;
  if (metadata.type === '3') return 12;
  if (interval === 'year') return 12;
  if (interval === 'month') return intervalCount;
  return 1;
};

const getTotalAmount = (plan) => {
  if (plan.unit_amount != null) return plan.unit_amount / 100;
  if (plan.amount != null) return plan.amount / 100;
  return 0;
};

export const parsePlanDisplay = (plan) => {
  const { metadata = {} } = plan;
  let functionalities = [];

  if (metadata.functionalities) {
    try {
      functionalities = JSON.parse(metadata.functionalities);
    } catch (e) {
      functionalities = [];
    }
  }

  const billingMonths = getBillingMonths(metadata, plan);
  const unitTotal = getTotalAmount(plan);
  const costoFromMeta = metadata.costoMensile
    ? parseFloat(String(metadata.costoMensile).replace(',', '.'))
    : null;

  // Su Stripe il campo costoMensile a volte contiene il totale del periodo (es. annuale)
  let periodTotal = unitTotal;
  if (billingMonths > 1) {
    if (periodTotal > 0) {
      // ok
    } else if (costoFromMeta != null && costoFromMeta > 0) {
      periodTotal = costoFromMeta;
    }
  } else if (periodTotal <= 0 && costoFromMeta != null && costoFromMeta > 0) {
    periodTotal = costoFromMeta;
  }

  let monthlyPrice = 0;
  if (billingMonths > 1 && periodTotal > 0) {
    monthlyPrice = periodTotal / billingMonths;
  } else if (costoFromMeta != null && costoFromMeta > 0) {
    monthlyPrice = costoFromMeta;
  } else {
    monthlyPrice = periodTotal;
  }

  const recurring = plan.recurring || {};
  const interval = recurring.interval || plan.interval;
  const intervalCount = recurring.interval_count || plan.interval_count || 1;

  return {
    id: plan.id,
    name: metadata.name || plan.nickname || 'Piano',
    subtitle: metadata.year || getIntervalLabel(interval, intervalCount),
    price: monthlyPrice,
    totalAmount: periodTotal,
    billingMonths,
    discount: metadata.discount || null,
    functionalities,
    amount: periodTotal,
    isPremium: isPremiumPriceId(plan.id, metadata),
    intervalType: metadata.type || null,
    intervalLabel: getIntervalShortLabel(metadata.type, interval, intervalCount),
  };
};

export const INTERVAL_ORDER = ['4', '1', '3'];
export const EXCLUDED_INTERVAL_TYPES = ['2'];

export const LEGACY_BASIC_PRICE_IDS = new Set([
  'price_1KtvEAD1ZRY9XjQLvVu7sTPF',
  'price_1KvJ5xD1ZRY9XjQLbzVfBnxK',
  'price_1KtvEOD1ZRY9XjQLosJEXarn',
  'price_1KtvELD1ZRY9XjQLaCjBhlbT',
]);

/** Basic del listino commerciale attuale (senza semestrale). */
export const STANDARD_BASIC_PRICE_IDS = new Set([
  'price_1TlR2FD1ZRY9XjQL9TgcsEnc', // mensile
  'price_1TlR2FD1ZRY9XjQLQWo6gEz2', // trimestrale
  'price_1TlR2GD1ZRY9XjQLShfIggJc', // annuale
  ...LEGACY_BASIC_PRICE_IDS,
]);

/**
 * Listino commerciale attuale (Basic + Premium, senza semestrale).
 * Serve da fallback quando getDaysLeft restituisce solo il price custom dell'abbonato
 * (es. planMode=custom con un solo id in `prezzi`) così si può comunque cambiare frequenza.
 */
export const STANDARD_CATALOG_PRICE_IDS = [
  // Basic
  'price_1TlR2FD1ZRY9XjQL9TgcsEnc', // mensile
  'price_1TlR2FD1ZRY9XjQLQWo6gEz2', // trimestrale
  'price_1TlR2GD1ZRY9XjQLShfIggJc', // annuale
  // Premium
  'price_1TlR2GD1ZRY9XjQLjNt6pHyp', // mensile
  'price_1TlR2HD1ZRY9XjQLTgfvAaUY', // trimestrale
  'price_1KtvELD1ZRY9XjQLKGhm96hM', // annuale
];

/** Price ID Premium del listino commerciale (allineati a getActivePlan / navbar). */
export const STANDARD_PREMIUM_PRICE_IDS = new Set([
  'price_1TlR2GD1ZRY9XjQLjNt6pHyp', // mensile
  'price_1TlR2HD1ZRY9XjQLTgfvAaUY', // trimestrale
  'price_1KtvELD1ZRY9XjQLKGhm96hM', // annuale
]);

/** True se il price è Premium (metadata Stripe o listino commerciale noto). */
export const isPremiumPriceId = (priceId, metadata = {}) => {
  if (!priceId) return false;
  if (metadata?.tier === 'premium') return true;
  return STANDARD_PREMIUM_PRICE_IDS.has(String(priceId));
};

/** Solo price del listino commerciale Basic (ignora tier 50€ storici / custom). */
export const filterCommercialBasicPlans = (rawPlans = []) =>
  (rawPlans || []).filter((plan) => STANDARD_BASIC_PRICE_IDS.has(String(plan.id)));

/** Solo price del listino commerciale Premium. */
export const filterCommercialPremiumPlans = (rawPlans = [], premiumPriceIds = []) => {
  const premiumIds = new Set([
    ...STANDARD_PREMIUM_PRICE_IDS,
    ...(premiumPriceIds || []).map(String),
  ]);
  return (rawPlans || []).filter((plan) => {
    if (premiumIds.has(String(plan.id))) return true;
    return parsePlanDisplay(plan).isPremium;
  });
};

/** Unisce prezzi BE + listino standard + eventuale priceId corrente. */
export const resolvePlanCatalogIds = (products = {}, extraIds = []) =>
  [
    ...new Set([
      ...(products.prezzi || []),
      ...(products.prezziPremium || []),
      ...STANDARD_CATALOG_PRICE_IDS,
      ...extraIds.filter(Boolean),
    ]),
  ];

// Percentuali mostrate in UI (allineate al listino commerciale)
export const INTERVAL_SAVINGS_PERCENT = {
  '1': 10,
  '3': 17,
};

export const isAvailableIntervalPlan = (plan) => {
  const { intervalType } = parsePlanDisplay(plan);
  if (EXCLUDED_INTERVAL_TYPES.includes(intervalType)) return false;
  const recurring = plan.recurring || {};
  const intervalCount = recurring.interval_count || plan.interval_count;
  if (recurring.interval === 'month' && intervalCount === 6) return false;
  if (plan.interval === 'month' && plan.interval_count === 6) return false;
  return true;
};

export const filterAvailablePlans = (rawPlans) =>
  (rawPlans || []).filter(isAvailableIntervalPlan);

export const resolveSelectableIntervalType = (intervalType, availablePlans) => {
  if (
    intervalType &&
    availablePlans.some((plan) => parsePlanDisplay(plan).intervalType === intervalType)
  ) {
    return intervalType;
  }
  const first = availablePlans[0];
  return first ? parsePlanDisplay(first).intervalType : null;
};

export const getPlanSavingsPercent = (tierPlans, targetPlan) => {
  const target = parsePlanDisplay(targetPlan);
  if (!target.intervalType || target.intervalType === '4') return null;

  if (INTERVAL_SAVINGS_PERCENT[target.intervalType] != null) {
    return INTERVAL_SAVINGS_PERCENT[target.intervalType];
  }

  const fromMetadata = target.discount?.match(/(\d+)\s*%/);
  if (fromMetadata) {
    return parseInt(fromMetadata[1], 10);
  }

  const monthlyPlan = (tierPlans || []).find((plan) => parsePlanDisplay(plan).intervalType === '4');
  if (!monthlyPlan) return null;

  const monthly = parsePlanDisplay(monthlyPlan);
  if (!monthly.price || monthly.price <= 0 || target.price >= monthly.price) return null;

  return Math.round((1 - target.price / monthly.price) * 100);
};

export const pickPlanForInterval = (planA, planB, currentPriceId) => {
  if (planA.id === currentPriceId) return planA;
  if (planB.id === currentPriceId) return planB;

  const aLegacy = LEGACY_BASIC_PRICE_IDS.has(planA.id);
  const bLegacy = LEGACY_BASIC_PRICE_IDS.has(planB.id);
  if (!aLegacy && bLegacy) return planA;
  if (aLegacy && !bLegacy) return planB;

  const priceA = parsePlanDisplay(planA).price;
  const priceB = parsePlanDisplay(planB).price;
  return priceA <= priceB ? planA : planB;
};

export const dedupePlansByInterval = (rawPlans, currentPriceId) => {
  const byInterval = new Map();

  filterAvailablePlans(rawPlans || []).forEach((rawPlan) => {
    const { intervalType } = parsePlanDisplay(rawPlan);
    const key = intervalType || 'unknown';
    const existing = byInterval.get(key);
    byInterval.set(
      key,
      existing ? pickPlanForInterval(existing, rawPlan, currentPriceId) : rawPlan,
    );
  });

  return INTERVAL_ORDER.map((intervalType) => byInterval.get(intervalType)).filter(Boolean);
};

export const buildIntervalOptions = (rawPlans, { currentPriceId } = {}) => {
  const available = dedupePlansByInterval(rawPlans, currentPriceId);
  return available.map((rawPlan) => {
    const display = parsePlanDisplay(rawPlan);
    return {
      rawPlan,
      display,
      intervalType: display.intervalType,
      intervalLabel: display.intervalLabel,
      planId: display.id,
      savingsPercent: getPlanSavingsPercent(available, rawPlan),
    };
  });
};

export const getIntervalShortLabel = (type, interval, count = 1) => {
  if (type === '4' || (interval === 'month' && count === 1)) return 'Mensile';
  if (type === '1' || (interval === 'month' && count === 3)) return 'Trimestrale';
  if (type === '2' || (interval === 'month' && count === 6)) return 'Semestrale';
  if (type === '3' || interval === 'year') return 'Annuale';
  return getIntervalLabel(interval, count);
};

export const sortPlansByInterval = (rawPlans) =>
  filterAvailablePlans(rawPlans).sort((a, b) => {
    const orderA = INTERVAL_ORDER.indexOf(parsePlanDisplay(a).intervalType);
    const orderB = INTERVAL_ORDER.indexOf(parsePlanDisplay(b).intervalType);
    return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
  });

export const sortPlansForDisplay = (rawPlans) =>
  [...rawPlans].sort((a, b) => parsePlanDisplay(a).price - parsePlanDisplay(b).price);

export const formatEuroAmount = (amount, { fromCents = false } = {}) => {
  const value = fromCents ? amount / 100 : amount;
  return value.toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const getPlanPeriodLabel = (planDisplay) => {
  if (!planDisplay || planDisplay.billingMonths <= 1) return null;
  if (planDisplay.intervalType === '3' || planDisplay.billingMonths === 12) {
    return 'ogni anno';
  }
  return `ogni ${planDisplay.billingMonths} mesi`;
};

export const getPlanBillingLabel = (planDisplay) => {
  if (!planDisplay) return '+ IVA / mese';
  if (planDisplay.billingMonths > 1) {
    return `+ IVA ${getPlanPeriodLabel(planDisplay)}`;
  }
  return '+ IVA / mese';
};

export const getLowestMonthlyPrice = (rawPlans) => {
  if (!rawPlans?.length) return null;
  return Math.min(...rawPlans.map((plan) => parsePlanDisplay(plan).price));
};

export const BASIC_BENEFITS_FALLBACK = [
  'Gestione prenotazioni',
  'Agenda e storico clienti',
  'Shop con limite prodotti',
  'Commissioni pagamento standard',
  'Assistenza con ticket',
  'Report essenziali',
];

export const PREMIUM_BENEFITS_FALLBACK = [
  'Tutto ciò che hai nel Basic',
  'Account staff',
  'Statistiche avanzate',
  'Gestione delle spese e degli utili',
  'Shop illimitato',
  'Assistenza telefonica',
  'Riduzione commissioni pagamento',
];

/** Righe confronto Basic × Premium (stesso ordine, no feature inventate). */
export const buildTierComparisonRows = () => {
  const premiumExtra = PREMIUM_BENEFITS_FALLBACK.filter(
    (item) => !/tutto ciò che hai nel basic/i.test(item),
  );
  return [
    ...BASIC_BENEFITS_FALLBACK.map((label) => {
      // Su Premium sostituite da voci dedicate
      if (
        /shop con limite|assistenza con ticket|commissioni pagamento standard|report essenziali/i.test(
          label,
        )
      ) {
        return { label, basic: true, premium: false };
      }
      return { label, basic: true, premium: true };
    }),
    ...premiumExtra.map((label) => ({ label, basic: false, premium: true })),
  ];
};

export const getAlignedTierFeatureRows = (isPremium) =>
  buildTierComparisonRows().map((row) => ({
    label: row.label,
    included: isPremium ? row.premium : row.basic,
  }));

/**
 * Esclusive Premium (senza la riga bundle).
 * La matrice completa resta in buildTierComparisonRows().
 */
export const getPremiumExclusiveBenefits = () =>
  PREMIUM_BENEFITS_FALLBACK.filter(
    (item) => !/tutto ciò che hai nel basic/i.test(item),
  );

/**
 * Feature Basic da mostrare sulla card Premium.
 * Esclude voci sostituite dalle esclusive Premium.
 */
export const getBasicIncludedHighlights = () =>
  BASIC_BENEFITS_FALLBACK
    .filter(
      (label) =>
        !/shop con limite|assistenza con ticket|commissioni pagamento standard|report essenziali/i.test(
          label,
        ),
    )
    .map((label) => ({
      label,
      included: true,
    }));

/**
 * Highlight card:
 * - Basic: essenziali + esclusive Premium barrate
 * - Premium: base + esclusive (upgrade vs in aggiunta)
 */
const PREMIUM_UPGRADE_LABEL_RE =
  /statistiche avanzate|shop illimitato|assistenza telefonica|riduzione commissioni/i;

export const getTierCardHighlights = (isPremium) => {
  if (!isPremium) {
    return [
      ...BASIC_BENEFITS_FALLBACK.slice(0, 5).map((label) => ({
        label,
        included: true,
      })),
      ...getPremiumExclusiveBenefits().map((label) => ({
        label,
        included: false,
      })),
    ];
  }

  return [
    ...getBasicIncludedHighlights(),
    ...getPremiumExclusiveBenefits().map((label) => {
      const isUpgrade = PREMIUM_UPGRADE_LABEL_RE.test(label);
      return {
        label,
        included: true,
        isUpgrade,
        isAddition: !isUpgrade,
      };
    }),
  ];
};

export const getTierBenefits = (_rawPlans, isPremium) =>
  isPremium ? PREMIUM_BENEFITS_FALLBACK : BASIC_BENEFITS_FALLBACK;

export const findPlanByPriceId = (rawPlans, priceId) =>
  rawPlans.find((plan) => plan.id === priceId) || null;

export const filterPlansByTier = (rawPlans, isPremium, premiumPriceIds = []) => {
  const premiumIds = new Set(resolvePremiumPriceIds(rawPlans, premiumPriceIds));

  return rawPlans.filter((plan) => {
    const { isPremium: isPremiumMeta } = parsePlanDisplay(plan);
    const isPremiumPlan = isPremiumMeta || premiumIds.has(plan.id);

    return isPremium ? isPremiumPlan : !isPremiumPlan;
  });
};

export const resolvePremiumPriceIds = (rawPlans = [], premiumPriceIds = []) => {
  const fromMetadata = (rawPlans || [])
    .filter((plan) => parsePlanDisplay(plan).isPremium)
    .map((plan) => plan.id);

  // Listino Premium noto + BE + metadata: evita Basic falso se tier Stripe manca
  const fromStandard = [...STANDARD_PREMIUM_PRICE_IDS];

  if (premiumPriceIds?.length || fromMetadata.length || fromStandard.length) {
    return [...new Set([...(premiumPriceIds || []), ...fromMetadata, ...fromStandard])];
  }

  const monthlyPlans = (rawPlans || []).filter(
    (plan) => parsePlanDisplay(plan).intervalType === '4',
  );
  if (monthlyPlans.length < 2) return [];

  const priced = monthlyPlans
    .map((plan) => ({ id: plan.id, price: parsePlanDisplay(plan).price }))
    .sort((a, b) => a.price - b.price);
  const lowest = priced[0]?.price;
  const highest = priced[priced.length - 1]?.price;

  if (!lowest || !highest || highest <= lowest * 1.15) return [];

  const threshold = (lowest + highest) / 2;
  return (rawPlans || [])
    .filter((plan) => parsePlanDisplay(plan).price >= threshold)
    .map((plan) => plan.id);
};

export const getTierLabel = (isPremium) => (isPremium ? 'Premium' : 'Basic');

export const isCustomPlanMode = (planMode) => planMode === 'custom';

export const getPlanTierLabel = (plan, { isCustomPlan, isPremium, allowCatalogLabel } = {}) => {
  // Piano "custom" ma presente nel listino a più frequenze: etichetta di tier, non il nome Stripe ("Mensile")
  if (isCustomPlan && isPremium && !allowCatalogLabel) {
    return plan?.name || 'Piano personalizzato';
  }
  if (isCustomPlan && !allowCatalogLabel) {
    return getTierLabel(false);
  }
  return getTierLabel(isPremium);
};

const getIntervalLabel = (interval, count = 1) => {
  if (interval === 'month' && count === 1) return 'Ogni mese';
  if (interval === 'month' && count === 3) return 'Ogni 3 mesi';
  if (interval === 'year') return 'Ogni anno';
  return `Ogni ${count} ${interval}`;
};

export const getPlanActionLabel = (currentPriceId, planId, isSubscribed) => {
  if (!currentPriceId) return 'Scegli';
  if (currentPriceId === planId) return 'Piano attuale';
  if (!isSubscribed) return 'Scegli';
  return 'Upgrade';
};

export const isPlanSelectable = (currentPriceId, planId) => {
  return !currentPriceId || currentPriceId !== planId;
};

export const isUpgradePlan = (currentPlan, targetPlan) => {
  if (!currentPlan || !targetPlan) return false;
  const current = parsePlanDisplay(currentPlan);
  const target = parsePlanDisplay(targetPlan);
  if (current.id === target.id) return false;
  if (!current.isPremium && target.isPremium) return true;
  if (current.isPremium === target.isPremium && target.price > current.price) return true;
  return target.amount > current.amount;
};
